import { Star } from 'lucide-react'
import { cn } from '@/lib/cn'
import { moodLabel } from './describe'
import styles from './RatingStars.module.css'

interface RatingStarsProps {
  /** 1–5. Anything else renders five empty stars. */
  value: number
  size?: 'sm' | 'md' | 'lg'
  /** Adds the word for the score after the stars — "Good", "Poor". */
  withLabel?: boolean
  className?: string
}

/**
 * A score as stars, the way the customer gave it.
 *
 * Amber for the filled stars everywhere on the dashboard, matching the app:
 * the brand red is reserved for actions, and a red star would read as a
 * warning rather than a compliment.
 */
export function RatingStars({ value, size = 'md', withLabel = false, className }: RatingStarsProps) {
  const score = Number.isFinite(value) ? Math.max(0, Math.min(5, Math.round(value))) : 0
  return (
    <span
      className={cn(styles.stars, styles[size], className)}
      role="img"
      aria-label={score === 0 ? 'No rating' : `${score} of 5 stars, ${moodLabel(score)}`}
    >
      {Array.from({ length: 5 }, (_, index) => (
        <Star
          key={index}
          aria-hidden="true"
          className={cn(styles.star, index < score && styles.filled)}
          strokeWidth={index < score ? 0 : 1.75}
        />
      ))}
      {withLabel ? <span className={styles.label}>{moodLabel(score)}</span> : null}
    </span>
  )
}

interface RatingFigureProps {
  /** The average, or null for none. */
  average: number | null
  count: number
  className?: string
}

/**
 * An average with its sample size, for a table cell: "4.6" in bold, a small
 * amber star, and "12 ratings" underneath. A driver nobody has rated shows
 * "No ratings yet" rather than a zero that reads as the worst possible score.
 */
export function RatingFigure({ average, count, className }: RatingFigureProps) {
  if (count === 0 || average == null) {
    return <span className={cn(styles.figureEmpty, className)}>No ratings yet</span>
  }
  return (
    <span className={cn(styles.figure, className)}>
      <span className={styles.figureValue}>
        <Star aria-hidden="true" className={cn(styles.star, styles.filled, styles.figureStar)} strokeWidth={0} />
        {average.toFixed(1)}
      </span>
      <span className={styles.figureCount}>
        {count} {count === 1 ? 'rating' : 'ratings'}
      </span>
    </span>
  )
}
